import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { ConfirmationPurchaseData } from '../../../models/page/confirmationPageState';
import { StoreContext } from '../../../models/store/context';
import { StoreEvent } from '../../../enums/storeEvent';
import { CheckoutSuccess } from '../../../pages/CheckoutPage/CheckoutResponses';
import { adobeTrackPurchaseCompletion } from '../../../services/adobeTags/track';
import { postRegistria } from '../../../services/api/postRegistria';
import { saveSession } from '../../../services/session/save';
import { log } from '@domgen/shared/utils/log';

/**
 * Finalizes successful checkout by deriving purchase confirmation data.
 *
 * Persists final confirmation state, triggers downstream tracking, and routes to
 * confirmation. Also posts contract registration when a plan number is available.
 *
 * @param context Current store context.
 * @param event Event optionally containing checkout success payload.
 * @param enqueue Store enqueue helpers used for side effects and navigation emission.
 * @returns Updated context when success data exists; otherwise no state change.
 */
export const checkoutSuccess = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data?: CheckoutSuccess },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext | void => {
  log.debug({ message: StoreEvent.CHECKOUT_SUCCESS, data: event.data });
  const quote = context.quote?.data;
  const checkoutSuccessData = event.data;

  if (quote && checkoutSuccessData) {
    const paymentType = checkoutSuccessData.billing.paymentMethodUsed;
    const mappedPaymentType = paymentType === 'card' ? 'cc' : 'ach';
    const planNumber = checkoutSuccessData.contracts[0].contractId;
    const transactionId = checkoutSuccessData.transactionId;

    const { monthly_subs } = quote.offer.contracts[0].contractAttributes;

    const offerTakenUp = monthly_subs?.[mappedPaymentType];

    const purchaseData: ConfirmationPurchaseData = {
      planNumber,
      transactionId,
      paymentType,
      mappedPaymentType,
      netFirstPaymentAmountFromPurchase: offerTakenUp?.netFirstPaymentAmount,
      netFirstPaymentAmountAfterPromotionalPeriodFromPurchase:
        offerTakenUp?.discountProfileAttributes?.netFirstPaymentAmountAfterPromotionalPeriod,
      discountCodeFromPurchase:
        offerTakenUp?.discountProfileAttributes?.[
          mappedPaymentType === 'cc' ? 'ccDiscountCodeApplied' : 'achDiscountCodeApplied'
        ],
      discountTermFromPurchase: offerTakenUp?.discountProfileAttributes?.discountTerm,
    };

    if (planNumber) {
      enqueue.effect(async () => {
        try {
          await postRegistria({
            locale: navigator.language.toLowerCase(),
            contractId: planNumber,
          });
        } catch (error) {
          log.error({
            message: 'Failed to post Registria contract registration',
            data: error,
          });
        }
      });
    }

    const guarantee = context.quote?.data?.items?.[0]?.guarantee;

    const { ...confirmation } = context.confirmation || {};

    const updatedContext = {
      ...context,
      quote: undefined,
      checkout: undefined,
      confirmation: {
        ...confirmation,
        purchaseData,
        regOnlyData: undefined,
        applianceDetails: {
          ...confirmation?.applianceDetails,
          guarantee: guarantee ?? 12,
        },
      },
    };

    saveSession(updatedContext);
    adobeTrackPurchaseCompletion({
      modelNumber: context.form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] ?? '',
      espSku: context.form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] ?? '',
      productSku: [],
    });
    enqueue.emit.navigate({ path: AppRoute.CONFIRMATION, replace: true });

    return updatedContext;
  }
};
